import isEqual from "lodash/isEqual"
import { useEffect, useState, useCallback } from "react"
import { ActionMeta, GroupBase, OnChangeValue, Props } from "react-select"
import BaseComponents from "./components"
import { formatOptionLabel, hasLabel } from "./utils"

export const useSelectProps = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>({
  components = {},
  isMulti,
  closeMenuOnScroll = true,
  hideSelectedOptions = false,
  closeMenuOnSelect,
  label,
  options,
  onChange: changeFn,
  styles,
  ...props
}: Props<Option, IsMulti, Group>): Props<Option, IsMulti, Group> => {
  const [stateOptions, setStateOptions] = useState<Option[]>(
    Array.isArray(options) ? [...options] : []
  )

  const sortOptions = useCallback((values: Option[], currentOptions: Option[]) => {
    const tmp = values || []

    const unselectedOptions = currentOptions.filter(
      (option) => !tmp.find((op) => isEqual(op, option))
    )

    const orderedNewOptions = tmp.sort((a, b) => {
      if (hasLabel(a) && hasLabel(b)) {
        return a.label > b.label ? 1 : b.label > a.label ? -1 : 0
      }

      return 0
    })

    setStateOptions(orderedNewOptions.concat(unselectedOptions as Option[]))
  }, [])

  useEffect(() => {
    if (isMulti && options && props.value) {
      // When isMulti is true, props.value is typed as readonly array which causes typing mismatch.
      const valueArray = Array.isArray(props.value) ? [...props.value] : []
      const optionsArray = Array.isArray(options) ? [...options] : []
      sortOptions(valueArray, optionsArray)
    } else {
      const optionsArray = Array.isArray(options) ? [...options] : []
      setStateOptions(optionsArray)
    }
  }, [options, props.value, isMulti, sortOptions])

  const onChange = (
    newValue: OnChangeValue<Option, IsMulti>,
    actionMeta: ActionMeta<Option>
  ) => {
    if (isMulti && Array.isArray(newValue)) {
      sortOptions(newValue, stateOptions)
    }

    if (changeFn) {
      changeFn(newValue, actionMeta)
    }
  }

  return {
    label,
    components: { ...BaseComponents, ...components },
    styles: {
      menuPortal: (base: any) => ({ ...base, zIndex: 60, pointerEvents: "auto" }),
      ...styles,
    },
    isMulti,
    closeMenuOnScroll: true,
    closeMenuOnSelect:
      closeMenuOnSelect !== undefined ? closeMenuOnSelect : isMulti !== true,
    hideSelectedOptions,
    menuPosition: "fixed",
    maxMenuHeight: 188,
    formatOptionLabel,
    options: stateOptions,
    onChange,
    ...props,
  }
}
